import { useEffect, useState } from "react";
import { Dice5, RotateCw, Activity, AlertTriangle, ShieldAlert } from "lucide-react";
import { api } from "../../lib/api";

export default function AdminSportsbook() {
  const [overview, setOverview] = useState(null);
  const [audit, setAudit] = useState(null);
  const [wsbfId, setWsbfId] = useState("");
  const [playerLedger, setPlayerLedger] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/admin/sportsbook/overview").then((r) => setOverview(r.data)).catch(() => setError("overview"));
    api.get("/admin/sportsbook/audit").then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const recompute = async () => {
    if (!wsbfId.trim()) return;
    setRecomputing(true); setError("");
    try {
      await api.post(`/admin/sportsbook/recompute/${wsbfId.trim()}`);
      const r = await api.get(`/admin/sportsbook/ledger/${wsbfId.trim()}`);
      setPlayerLedger(r.data);
      load();
    } catch {
      setError("recompute");
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sportsbook-page">
      <div className="flex items-center gap-3">
        <Dice5 className="w-6 h-6 text-[#E5B842]" />
        <h1 className="font-heading text-2xl font-black uppercase tracking-tight text-slate-50">Sportsbook Engine</h1>
        <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500">· {overview?.calculation_version || "SPORTSBOOK_V1"}</span>
      </div>
      {error && <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 p-3 text-sm" data-testid="admin-sportsbook-error">{error}</div>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-sportsbook-overview">
        <OvCard label="Events accepted" value={overview?.events_accepted ?? "—"} testid="ov-events" />
        <OvCard label="Ledger active" value={overview?.ledger_active ?? "—"} testid="ov-ledger-active" />
        <OvCard label="Corrections" value={overview?.corrections_logged ?? "—"} testid="ov-corrections" />
        <OvCard label="Rated players" value={overview?.rated_players ?? "—"} testid="ov-rated" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-sportsbook-maturity">
        {["NEW", "PROVISIONAL", "RANKED", "ESTABLISHED"].map((m) => (
          <OvCard key={m} label={m} value={overview?.maturity?.[m] ?? "—"} testid={`ov-mat-${m}`} />
        ))}
      </div>
      <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-[#E5B842]" />
          <div className="font-heading text-sm font-black uppercase tracking-wider text-slate-100">Historical event audit</div>
        </div>
        {audit ? (
          <>
            <div className="text-xs text-slate-400 font-mono mb-2" data-testid="audit-totals">
              Accepted: <b className="text-slate-200">{audit.totals.ACCEPTED}</b> · Valid for V1: <b className="text-emerald-300">{audit.totals.VALID}</b>
            </div>
            {Object.keys(audit.reasons || {}).length > 0 && (
              <div className="text-[11px] font-mono text-amber-300 flex items-start gap-2" data-testid="audit-reasons">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>Skip reasons: {Object.entries(audit.reasons).map(([k, v]) => `${k}×${v}`).join(" · ")}</div>
              </div>
            )}
          </>
        ) : <div className="text-xs text-slate-500 font-mono">Loading…</div>}
      </div>
      <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-4 h-4 text-[#E5B842]" />
          <div className="font-heading text-sm font-black uppercase tracking-wider text-slate-100">Recompute / Inspect player</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input data-testid="admin-sportsbook-wsbf" value={wsbfId} onChange={(e) => setWsbfId(e.target.value)}
            placeholder="WSTF-GRE-000001"
            className="flex-1 min-w-[220px] bg-[#0a0a0a] border border-[rgba(212,175,55,0.2)] rounded-lg px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:border-[#E5B842]/60" />
          <button data-testid="admin-sportsbook-recompute" onClick={recompute} disabled={!wsbfId || recomputing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-heading font-bold uppercase tracking-wide bg-[#E5B842] text-black disabled:opacity-50 hover:bg-[#F0D78A]"
            style={{ transition: "background-color 0.15s ease" }}>
            <RotateCw className={`w-3.5 h-3.5 ${recomputing ? "animate-spin" : ""}`} /> Recompute
          </button>
        </div>
        {playerLedger && (
          <div className="mt-4 overflow-x-auto" data-testid="admin-sportsbook-ledger-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono bg-[#070707]">
                  <th className="text-left px-3 py-2">Event</th>
                  <th className="text-left px-3 py-2">Subtype</th>
                  <th className="text-left px-3 py-2">Result</th>
                  <th className="text-right px-3 py-2">Odds</th>
                  <th className="text-left px-3 py-2">Tier</th>
                  <th className="text-right px-3 py-2">Points</th>
                  <th className="text-left px-3 py-2">State</th>
                </tr>
              </thead>
              <tbody>
                {(playerLedger.entries || []).map((e) => (
                  <tr key={e.id} className="border-t border-[rgba(212,175,55,0.08)]" data-testid={`ledger-${e.event_id}`}>
                    <td className="px-3 py-2 font-mono text-slate-300 text-[11px]">{e.event_id}</td>
                    <td className="px-3 py-2 text-slate-300">{e.subtype}</td>
                    <td className={`px-3 py-2 font-bold ${e.result === "WON" ? "text-emerald-400" : e.result === "LOST" ? "text-rose-400" : "text-slate-400"}`}>{e.result}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-200">{Number(e.actual_odds).toFixed(2)}</td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{e.difficulty_tier} · {e.difficulty_label}</td>
                    <td className={`px-3 py-2 text-right font-heading font-black tabular-nums ${e.coupon_points > 0 ? "text-[#E5B842]" : e.coupon_points < 0 ? "text-rose-400" : "text-slate-400"}`}>
                      {e.coupon_points > 0 ? `+${e.coupon_points.toFixed(1)}` : e.coupon_points.toFixed(1)}
                    </td>
                    <td className={`px-3 py-2 font-mono text-[11px] ${e.state === "ACTIVE" ? "text-emerald-400" : "text-amber-400"}`}>{e.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(playerLedger.entries || []).length === 0 && <div className="text-center py-6 text-slate-500 font-mono text-xs uppercase tracking-widest">No ledger entries</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function OvCard({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-1.5">{label}</div>
      <div className="font-heading text-2xl font-black text-slate-50 tabular-nums leading-none">{value}</div>
    </div>
  );
}
